import React from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";

const BRAND_COLOR = '#623CE7';
const BRAND_COLOR_HOVER = '#4f2fc2';
const BRAND_COLOR_LIGHT = '#F4F1FD';

// MailIllustration Component
const MailIllustration = ({ className }) => {
  return (
    <svg 
      viewBox="0 0 200 200" 
      fill="none" 
      xmlns="http://www.w3.org/2000/svg" 
      className={className}
      aria-hidden="true"
    >
      {/* Soft Background Blob */}
      <circle cx="100" cy="100" r="70" fill={BRAND_COLOR_LIGHT} />

      {/* Paper Element */}
      <rect x="72" y="55" width="56" height="50" rx="2" fill="#FFFFFF" stroke="#E5E7EB" strokeWidth="2" />
      <line x1="84" y1="70" x2="116" y2="70" stroke="#E5E7EB" strokeWidth="2" strokeLinecap="round" />
      <line x1="84" y1="80" x2="108" y2="80" stroke="#E5E7EB" strokeWidth="2" strokeLinecap="round" />

      {/* Envelope Container */}
      <rect x="55" y="85" width="90" height="60" rx="4" fill="#FFFFFF" stroke="#1F2937" strokeWidth="2" />
      
      {/* Envelope Flap Lines */}
      <path 
        d="M55 85 L100 115 L145 85" 
        stroke="#1F2937" 
        strokeWidth="2" 
        strokeLinejoin="round" 
        fill="none"
      />

      {/* Notification / Alert Bubble */}
      <circle cx="145" cy="70" r="14" fill={BRAND_COLOR} stroke="#FFFFFF" strokeWidth="3" />
      
      {/* Simple Question Mark */}
      <path 
        d="M142.5 66C142.5 64.5 143.5 63.5 145 63.5C146.5 63.5 147.5 64.5 147.5 66C147.5 67.5 145 68.5 145 70.5V71.5" 
        stroke="#FFFFFF" 
        strokeWidth="2.5" 
        strokeLinecap="round" 
      />
      <circle cx="145" cy="75.5" r="1.5" fill="#FFFFFF" />
    </svg>
  );
};

function PageNotFound() {
  const navigate = useNavigate();

  const handlePrimaryAction = () => {
    navigate("/mailboxes/INBOX");
  };

  const handleSupportAction = () => {
    window.open("https://help.grigora.co", "_blank");
  };

  const handleHelpAction = () => {
    window.open("https://help.grigora.co", "_blank");
  };

  return (
    <>
      <Helmet>
        <title>Page Not Found - Grigora Mail</title>
      </Helmet>
      <main className="min-h-screen w-full flex flex-col items-center justify-center bg-white p-6 sm:p-8">
        <div className="max-w-md w-full flex flex-col items-center text-center space-y-8">
          
          {/* Visual Direction: Minimal envelope themed illustration */}
          <div className="w-48 h-40 flex items-center justify-center">
            <MailIllustration className="w-full h-full drop-shadow-sm" />
          </div>

          {/* Content Section */}
          <div className="space-y-3">
            <h1 className="text-3xl font-semibold text-neutral-800 tracking-tight">
              We could not find this page
            </h1>
            <p className="text-neutral-600 text-base leading-relaxed">
              The link you followed may be expired, or the page might have been removed.
            </p>
          </div>

          {/* Primary Action */}
          <div className="w-full pt-2">
            <button
              onClick={handlePrimaryAction}
              className="inline-flex items-center justify-center px-6 py-3 rounded-lg text-sm font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 w-full sm:w-auto min-w-[200px] text-white hover:shadow-lg hover:shadow-indigo-200/50 transition-all duration-300 transform hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              style={{ 
                backgroundColor: BRAND_COLOR,
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = BRAND_COLOR_HOVER}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = BRAND_COLOR}
            >
              <span className="material-icons-outlined text-base mr-2">arrow_back</span>
              Return to dashboard
            </button>
          </div>

          {/* Secondary Actions */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8 pt-4 border-t border-neutral-100 w-full">
            <button 
              onClick={handleSupportAction}
              className="inline-flex items-center text-sm font-medium text-neutral-600 hover:text-indigo-600 transition-colors group"
            >
              <span className="material-icons-outlined text-base mr-2 text-neutral-400 group-hover:text-indigo-600">support</span>
              Contact support
            </button>
            
            <button 
              onClick={handleHelpAction}
              className="inline-flex items-center text-sm font-medium text-neutral-600 hover:text-indigo-600 transition-colors group"
            >
              <span className="material-icons-outlined text-base mr-2 text-neutral-400 group-hover:text-indigo-600">help_outline</span>
              Help center
            </button>
          </div>

          {/* Brand Footer - Subtle reassurance of product identity */}
          <div className="pt-12">
            <a 
              href="https://www.grigora.co"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs font-medium text-neutral-300 uppercase tracking-widest hover:text-indigo-600 transition-colors"
            >
              Grigora Mail
            </a>
          </div>

        </div>
      </main>
    </>
  );
}

export default PageNotFound;
